import { Modal, message as AntdMessage } from 'antd'
import Image from 'next/image'
import { FC } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import useSWRMutation from 'swr/mutation'
import { unstable_serialize } from 'swr/infinite'
import { twMerge } from 'tailwind-merge'
import { SMALL_IMG_WIDTH } from '@/constant/img'
import { USER_CHATTED_LIST_LIMIT } from '@/constant/chat'
import { characterProfileState } from 'Store/chat'
import { deleteChatConfirmModalVisibleState } from 'Store/modal'
import {
  deleteUserChattedCharacter,
  deleteUserChattedGame,
  deleteUserChattedStory,
  getUserChattedListKey,
} from 'Apis/chat'
import { transformImage } from 'Utils/img'
import LoadingImg from '@/icons/LoadingImg'
import { useSWRConfig } from 'swr'
import { userState } from 'Store/user'
import useI18n from 'Hooks/useI18n'

const LIMIT = USER_CHATTED_LIST_LIMIT

interface RecentTalkDeleteChatConfirmModal {
  onDelete: (delChatId: string) => void
}

const DeleteChatConfirmModal: FC<RecentTalkDeleteChatConfirmModal> = ({
  onDelete,
}: RecentTalkDeleteChatConfirmModal) => {
  const { t, lang } = useI18n()
  const [open, setOpen] = useRecoilState(deleteChatConfirmModalVisibleState)
  const user = useRecoilValue(userState)
  const character = useRecoilValue(characterProfileState)

  const { item_id, item_type, face_url, item_name, game_id } = character || {}
  const deleteId = String(item_type === 'game' ? game_id || item_id : item_id)

  const { trigger: triggerDelete, isMutating } = useSWRMutation(
    deleteId && item_type === 'character'
      ? {
          endpoint: '/user/chatted/character/delete',
          character_id: deleteId,
        }
      : deleteId && item_type === 'story'
        ? {
            endpoint: '/user/chatted/story/delete',
            character_id: deleteId,
          }
        : deleteId && item_type === 'game'
          ? {
              endpoint: '/user/chatted/game/delete',
              character_id: deleteId,
            }
          : null,
    ({ character_id }) =>
      item_type === 'character'
        ? deleteUserChattedCharacter({ character_id })
        : item_type === 'story'
          ? deleteUserChattedStory({ story_id: character_id })
          : deleteUserChattedGame({ game_id: character_id })
  )

  const { mutate } = useSWRConfig()

  const handleDelete = async () => {
    if (!deleteId) return
    try {
      await triggerDelete()
      mutate(
        unstable_serialize(
          user
            ? getUserChattedListKey({
                size: LIMIT,
                language_code: lang,
              })
            : () => undefined
        )
      )
      setOpen(false)
      onDelete(deleteId)
    } catch (e) {
      AntdMessage.error((e as Error)?.message || t('Deletion failure'))
    }
  }

  return (
    <Modal
      open={open}
      closable={false}
      title={null}
      footer={null}
      centered
      styles={{
        content: {
          backgroundColor: 'unset',
          padding: 0,
          borderRadius: 'unset',
          boxShadow: 'unset',
        },
      }}
      width={368}
      mask
      maskClosable={!isMutating}
      onCancel={() => setOpen(false)}
    >
      <div className="flex flex-col items-center rounded-[10px] border border-tertiary-border bg-[rgba(0,0,0,0.24)] bg-gradient-to-b from-[#180E1FCC] from-0% via-[#41234DCC] via-50% to-[#1D1515CC] to-100% px-5 pb-[35px] pt-[31px]">
        <div
          className="flex-0 mb-5 rounded-[18px] border border-tertiary-border p-[5px]"
          style={{
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(87,47,102,0.5) 50%, rgba(29,21,21,0.5) 100%)',
          }}
        >
          <div className="relative h-[80px] w-[80px] overflow-hidden rounded-full border border-tertiary-border">
            <Image
              unoptimized
              className="object-cover"
              src={transformImage(face_url, SMALL_IMG_WIDTH)}
              alt={item_name}
              fill={true}
            />
          </div>
        </div>
        <div className="mb-[45px] flex w-full flex-wrap justify-center px-[30px] text-sm leading-6 text-[rgba(255,255,255,0.7)]">
          <span>
            {t('Permanently delete the chat with Character', {
              character: item_name,
            })}
          </span>
        </div>
        <div className="flex gap-[10px] text-base">
          <button
            className={twMerge(
              'shadow-[0px 2px 4px 0px rgba(255,255,255,0.05)] hover: h-[42px] w-[159px] rounded-[60px] border border-[rgba(255,255,255,.5)] bg-[rgba(0,0,0,.2)] text-tertiary-text ',
              isMutating
                ? 'cursor-not-allowed opacity-70'
                : 'hover:border-[rgba(255,255,255,.8)] hover:text-white'
            )}
            onClick={
              isMutating
                ? () => {}
                : () => {
                    setOpen(false)
                  }
            }
          >
            {t('Cancel')}
          </button>
          <button
            className={twMerge(
              'shadow-[0px 2px 4px 0px rgba(255,255,255,0.05)] inline-flex h-[42px] w-[159px] items-center justify-center rounded-[60px] border border-[rgba(255,255,255,.5)] bg-[rgba(255,255,255,.2)] text-primary-text ',
              isMutating
                ? 'cursor-not-allowed opacity-70'
                : 'hover:border-[rgba(255,255,255,.8)]'
            )}
            onClick={isMutating ? () => {} : handleDelete}
          >
            {isMutating ? (
              <LoadingImg className="left-[-3px] top-[-3px] !h-5 !w-5 scale-[.6]" />
            ) : (
              t('Delete')
            )}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default DeleteChatConfirmModal
